import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { ResponsiveLine, Serie } from '@nivo/line'
import { Spinner, useTheme } from '@ss/meta-core-components'
import { LegendProps } from '@nivo/legends'
import {
  AxisLegend,
  IDimension,
  IMargin,
  INumberFormat,
  ITimeScale,
} from '@types'
import { initTheme, MAIN_PALETTE, NumberFormatter } from '@utils'
import { linearGradientDef } from '@nivo/core'
import { BasicTooltip } from '@nivo/tooltip'

interface Props {
  data: Serie[]
  dataCy?: string
  dimensions?: IDimension
  curve?: 'natural' | 'linear' | 'monotoneX'
  timeScale?: ITimeScale
  axisLegend?: AxisLegend
  disableLine?: boolean
  customColors?: string[]
  loadingSpinner?: JSX.Element
  showLegend?: boolean
  domain?: { max: number; min: number }
  yFormat?: INumberFormat
  area?: boolean
  margin?: IMargin
}

/**
 * Componente custom per un grafico di tipo Line
 * @param props.data {Array<IGenericObject<string | number>>}
 * @param props.dataCy {string=} support test e2e
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.curve {('natural' | 'linear' | 'monotoneX')= } tipo della curva con cui disegnare la line
 * @param props.timeScale {ITimeScale= } flag per time scale
 * @param props.axisLegend {({ x: string, y: string })= } legenda per il dato del asse x e del asse y
 * @param props.disableLine {boolean= } permette di non mostrare le linee del grafico
 * @param props.customColors {Array<string> } permette di personalizzare i colori
 * @param props.loadingSpinner {JSX.Element=} componente custo per lo spinner
 * @param props.showLegend {boolean=} flag per mostrare la legenda
 * @param props.domain {({ max: number, min: number })=} estremi del grafico
 * @param props.margin {IMargin=} permette di customizzare i margini
 * @param props.yFormat { INumberFormat=} formato dei valori sull asse y
 * @param props.area { boolean=} flag per l'area sotto la linea default false
 * @component
 */
export const Line: React.FC<Props> = (props) => {
  const {
    data,
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    curve = 'linear',
    timeScale,
    axisLegend = { x: undefined, y: undefined },
    margin,
    disableLine,
    customColors,
    loadingSpinner,
    showLegend = false,
    yFormat = 'KGM',
    domain,
    area = false,
  } = props

  const { type } = useTheme()

  const [ticks, setTicks] = useState<(string | number)[]>([])

  useEffect(() => {
    setTicks([])
    if (!data) return

    const flatKeys = data
      .map(({ data }) => data)
      .flat()
      .map(({ x }) => x as number)
      .sort()

    const newKey: (string | number)[] = []
    flatKeys.forEach((k, index) => {
      if (index % Math.round(flatKeys.length / 5) === 0) {
        newKey.push(k)
        return
      }
    })
    setTicks(newKey)
  }, [data])

  const timeAxis = timeScale
    ? {
        [`${timeScale.axis}Scale`]: {
          type: 'time',
          format: timeScale.format,
          useUTC: false,
          precision: 'day',
        },
        [`${timeScale.axis}Format`]: `time:${timeScale.format}`,
        [`axis${timeScale.axis === 'x' ? 'Bottom' : 'Left'}`]: {
          format: '%b %d',
          tickValues: 'every 2 days',
          legend: axisLegend.x || 'Time',
          legendPosition: 'middle',
          legendOffset: 46,
        },
      }
    : undefined

  const chartProps: any = {
    margin: margin || { top: 60, right: 140, bottom: 70, left: 90 },
    axisTop: null,
    axisRight: null,
    enableArea: area,
    areaOpacity: 0.25,
    enableGridX: !area,
    enableGridY: !area,
    axisBottom: {
      legend: axisLegend.x,
      legendPosition: 'middle',
      legendOffset: 48,
      tickValues: ticks,
      format: (value: number) => {
        if (value <= 0) return NumberFormatter(value, 'KGM')
        if (value < 0.0001) return NumberFormatter(value, 'EXP')
        if (value > 99999) return NumberFormatter(value, 'KGM')
        return value
      },
    },
    axisLeft: {
      orient: 'left',
      legend: axisLegend.y,
      legendPosition: 'middle',
      legendOffset: -65,
      format: (value: number) => {
        return NumberFormatter(value, yFormat)
      },
    },
    curve,
    data,
    fill: area ? [{ match: '*', id: 'gradientArea' }] : [],
    defs: [
      {
        id: 'gradientArea',
        type: 'linearGradient',
        colors: [
          { offset: 0, color: 'inherit' },
          { offset: 100, color: 'inherit' },
        ],
      },
    ],
    enableSlices: 'x',
    enablePoints: false,
    lineWidth: disableLine ? 0 : 3,
    pointSize: 10,
    colors: customColors || MAIN_PALETTE,
  }

  const legends: LegendProps[] = showLegend
    ? [
        {
          anchor: 'top-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.85,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ]
    : []

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}-line`}
    >
      {data.length === 0 ? (
        <Loading>
          <Loading>
            {loadingSpinner ? (
              loadingSpinner
            ) : (
              <Spinner data-cy={`${dataCy}-bar-loading`} />
            )}
          </Loading>
          <Loading>Updating chart...</Loading>
        </Loading>
      ) : (
        <ResponsiveLine
          {...chartProps}
          legends={legends}
          yScale={
            domain
              ? {
                  type: 'linear',
                  stacked: false,
                  min: domain.min,
                  max: domain.max,
                }
              : undefined
          }
          {...timeAxis}
          theme={initTheme(type)}
        />
      )}
    </Container>
  )
}

const Container = styled.div<{ height: string; width: string }>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  color: var(--text);

  & table {
    color: var(--background);
  }
`
const Loading = styled.div`
  display: grid;
  place-self: center;
  gap: 10px;
`
